import React, { useState } from 'react';
import logo from '../assets/EzNoteManagerPrologo.png';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../firebase';
import { signOut } from 'firebase/auth';
import GoogleSignIn from './GoogleSignIn';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faFileExport, faFileImport, faEllipsisV, faCircleQuestion } from '@fortawesome/free-solid-svg-icons';
import KeyboardShortcutGuide from './KeyboardShortcutGuide';
import ExportModal from './ExportModal';
import { useTheme } from '../context/ThemeContext';
import { Menu } from '@headlessui/react';
import EncryptionSettings from './EncryptionSettings';
import { useEncryption } from '../context/EncryptionContext';

const Header = ({
  onSearchChange,
  searchQuery = '',
  searchFilter = 'title',
  setSearchFilter,
  onClearFilters,
  triggerNewNote,
  setSelectedTag,
  setSelectedCategory,
  searchInputRef,
  onExport,
  onImport,
  notes = []
}) => {
  const [user] = useAuthState(auth);
  const [showShortcutGuide, setShowShortcutGuide] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const { theme, setTheme, themes } = useTheme();
  const isLightTheme = themes[theme]?.mode === 'light';
  const { encryptionPolicy, isEncryptionLocked } = useEncryption();

  const handleClearFilters = () => {
    onClearFilters();
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleExport = () => {
    setShowExportModal(true);
  };

  const handleCreateNote = () => {
    if (isEncryptionLocked) return;
    triggerNewNote();
  };

  const handleImport = () => {
    if (isEncryptionLocked) return;
    onImport();
  };

  return (
    <header className={`bg-transparent px-3 py-3 sm:px-4 flex flex-wrap items-center gap-x-4 gap-y-3 w-full ${isLightTheme ? 'text-gray-800' : 'text-white'}`}>
      <div className="flex min-w-0 max-w-full shrink-0 items-center">
        <img src={logo} alt="Logo" className="mr-2 h-12 w-12 shrink-0 sm:mr-4" />
        <h1 className="min-w-0 break-words font-sans text-lg font-bold leading-tight sm:text-2xl">
          EzNote<span className="text-yellow-500">Manager</span><span className="text-blue-500">Pro</span>
        </h1>
      </div>
      
      <div className="order-3 flex w-full lg:order-none lg:flex-1 lg:max-w-xl">
        <div className="flex w-full flex-wrap gap-2 sm:flex-nowrap">
          <button
            onClick={handleClearFilters}
            className={`shrink-0 rounded border px-2 py-1 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-yellow-400 ${
              isLightTheme
                ? 'border-gray-400/60 hover:bg-gray-800/10'
                : 'border-white/40 hover:bg-white/10'
            }`}
          >
            Clear Filters
          </button>
          <input
            ref={searchInputRef}
            type="text"
            placeholder={`Search notes by ${searchFilter.toLowerCase()}...`}
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className={`min-w-0 flex-1 rounded border p-1 text-sm ${
              isLightTheme
                ? 'bg-white border-gray-300 text-gray-800 placeholder-gray-500'
                : 'bg-gray-700 border-gray-300 text-white placeholder-gray-400'
            }`}
          />
          <select
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            className={`w-24 shrink-0 rounded border p-1 text-sm ${
              isLightTheme
                ? 'bg-white border-gray-300 text-gray-800'
                : 'bg-gray-700 border-gray-300 text-white'
            }`}
          >
            <option value="title">Title</option>
            <option value="tags">Tags</option>
            <option value="category">Category</option>
          </select>
        </div>
      </div>

      <div className="ml-0 flex w-full flex-wrap items-center gap-2 sm:ml-auto sm:w-auto sm:flex-nowrap">
        {user ? (
          <>
            <span className="hidden text-sm opacity-90 2xl:inline">Welcome, {user.displayName}</span>
            <div className={`flex w-full flex-wrap items-center gap-2 sm:w-auto sm:flex-nowrap sm:border-l sm:pl-3 ${isLightTheme ? 'border-gray-500/40' : 'border-white/30'}`}>
              <label className="sr-only" htmlFor="theme-picker">Theme</label>
              <select
                id="theme-picker"
                value={theme}
                onChange={(event) => setTheme(event.target.value)}
                className={`rounded border px-2 py-1 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-yellow-400 ${
                  isLightTheme
                    ? 'border-gray-400/60 bg-white/70 text-gray-800'
                    : 'border-white/40 bg-white/10 text-white'
                }`}
              >
                {Object.entries(themes).map(([themeId, themeConfig]) => (
                  <option key={themeId} value={themeId} style={{ backgroundColor: '#ffffff', color: '#1f2937' }}>
                    {themeConfig.label}
                  </option>
                ))}
              </select>
            <button
              className="bg-red-500 hover:bg-red-600 text-white font-semibold py-2 px-3 rounded text-sm"
              onClick={handleSignOut}
            >
              Sign Out
            </button>
            <button
              onClick={handleCreateNote}
              disabled={isEncryptionLocked}
              aria-describedby={isEncryptionLocked ? 'encryption-mutation-lock' : undefined}
              className="create-note-button bg-yellow-500 hover:bg-yellow-600 text-white font-bold py-2 px-4 rounded-full text-base disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FontAwesomeIcon icon={faPlus} className="mr-2" />
              Create Note
            </button>
            <button
              onClick={() => setShowShortcutGuide(true)}
              className="bg-blue-500 hover:bg-blue-600 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold text-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
              title="Keyboard Shortcuts"
            >
              ?
            </button>
            <Menu as="div" className="relative">
              <Menu.Button className={`p-2 hover:bg-opacity-80 rounded-lg transition-colors ${
                isLightTheme
                  ? 'text-gray-600 hover:text-gray-800 hover:bg-gray-100' 
                  : 'text-gray-200 hover:text-white hover:bg-gray-700'
              }`}>
                <FontAwesomeIcon icon={faEllipsisV} className="text-xl" />
              </Menu.Button>
              <Menu.Items className="absolute right-0 mt-2 w-48 origin-top-right bg-white dark:bg-gray-800 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                <Menu.Item>
                  {({ active }) => (
                    <button
                      onClick={handleExport}
                      className={`${
                        active ? 'bg-gray-100 dark:bg-gray-700' : ''
                      } group flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300`}
                    >
                      <FontAwesomeIcon icon={faFileExport} className="mr-3" />
                      Export Notes
                    </button>
                  )}
                </Menu.Item>
                <Menu.Item>
                  {({ active }) => (
                    <button
                      onClick={handleImport}
                      disabled={isEncryptionLocked}
                      className={`${
                        active ? 'bg-gray-100 dark:bg-gray-700' : ''
                      } group flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300`}
                    >
                      <FontAwesomeIcon icon={faFileImport} className="mr-3" />
                      Import Notes
                    </button>
                  )}
                </Menu.Item>
                <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
                <EncryptionSettings />
              </Menu.Items>
            </Menu>
            </div>
            {isEncryptionLocked && <span id="encryption-mutation-lock" role="status" className="sr-only">Note changes are temporarily unavailable while encryption is being verified or changed.</span>}
          </>
        ) : (
          <GoogleSignIn />
        )}
      </div>

      <KeyboardShortcutGuide 
        isOpen={showShortcutGuide} 
        onClose={() => setShowShortcutGuide(false)} 
      />

      <ExportModal
        isOpen={showExportModal}
        onRequestClose={() => setShowExportModal(false)}
        onExport={(format, selectedNotes) => onExport(format, selectedNotes, encryptionPolicy)}
        notes={notes}
      />
    </header>
  );
};

export default Header;
